/**
 * Multi-tab dynamic form engine.
 *
 * Owns values, validation, async checks, capacity state, navigation and final
 * submit. Rendering of individual controls is delegated to FieldRenderer.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { submitForm, validateCaptcha, validateRemote, validateUnique } from '../../api/forms-api';
import { useUiLocale } from '../../app/ui-locale';
import { useFeedback } from '../../components/Feedback';
import type { DynamicFormDefinition, FormField, FormRow, FormValues } from '../../models/form';
import { evaluateVisibility } from '../../utils/conditions';
import { applyCalculatedValues, createInitialValues, getTabFields, isDataField, mergeValues, normalizeValuesForSubmit } from '../../utils/form-data';
import { validateFieldSync, type ValidationError } from '../../utils/validation';
import {
  getSimpleCaptchaChallenge,
  markSimpleCaptchaVerified,
  normalizeSimpleCaptchaInput,
  resolveSimpleCaptchaMode
} from '../../utils/simple-captcha';
import { FieldRenderer } from './FieldRenderer';
import type { SubmissionFileAccess } from './controls/FileUploadControl';
import { publicCopy } from './public-copy';

interface Props {
  form: DynamicFormDefinition;
  initialValues?: FormValues;
  submittedFormId?: number;
  readOnly?: boolean;
  showAllReadOnlyTabs?: boolean;
  omitVerificationFields?: boolean;
  moduleId?: number;
  secureSubmissionId?: number;
  submissionFileAccess?: SubmissionFileAccess;
  onSubmitted?: (submitCode: string, values: FormValues) => void;
}

type ErrorMap = Record<string, ValidationError[]>;

function path(tabName: string, fieldName: string): string { return `${tabName}.${fieldName}`; }


function normalizeStepTitle(value: unknown): string {
  return String(value ?? '')
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/\s+/gu, ' ')
    .trim();
}

function isDefaultSingleStepTitle(value: unknown): boolean {
  const title = normalizeStepTitle(value);
  return /^(?:مرحله|گام)\s*(?:1|اول)$/u.test(title)
    || /^step\s*(?:1|one)$/iu.test(title)
    || /^étape\s*(?:1|une|première)$/iu.test(title);
}

function shouldShowTabHeading(form: DynamicFormDefinition, title: unknown): boolean {
  return form.Tabs.length > 1 || !isDefaultSingleStepTitle(title);
}

function tabVisualStyle(tab: DynamicFormDefinition['Tabs'][number]): CSSProperties | undefined {
  return tab.color || tab.textColor
    ? { backgroundColor: tab.color || undefined, borderColor: tab.color || undefined, color: tab.textColor || undefined }
    : undefined;
}

interface FormRowsProps {
  rows: FormRow[];
  tabName: string;
  values: FormValues;
  errors?: ErrorMap;
  readOnly: boolean;
  onFieldChange: (name: string, value: unknown) => void;
  onAction?: (kind: NonNullable<FormField['buttonAction']>) => void;
  interactionDisabled?: boolean;
  secureSubmissionId?: number;
  submissionFileAccess?: SubmissionFileAccess;
  omitVerificationFields?: boolean;
}

function isVerificationField(field: FormField): boolean {
  return field.type === 'captcha' || field.type === 'simplecaptcha';
}

function FormRows({ rows, tabName, values, errors, readOnly, onFieldChange, onAction, interactionDisabled, secureSubmissionId, submissionFileAccess = 'owner', omitVerificationFields = false }: FormRowsProps) {
  return <>
    {rows.map((row) => <div className="row df-form-row" key={row.id}>
      {row.columns.map((column) => <div className={`col-md-${column.span} df-form-col df-col-span-${column.span}`} key={column.id}>
        {column.fields.map((field) => omitVerificationFields && isVerificationField(field) ? null : <FieldRenderer
          key={field.id}
          field={field}
          tabName={tabName}
          value={values[tabName]?.[field.name]}
          values={values}
          errors={errors?.[path(tabName, field.name)]}
          errorMap={errors}
          readOnly={readOnly}
          onChange={(next) => onFieldChange(field.name, next)}
          onFieldChange={onFieldChange}
          onAction={onAction}
          interactionDisabled={interactionDisabled}
          secureSubmissionId={secureSubmissionId}
          submissionFileAccess={submissionFileAccess}
          omitVerificationFields={omitVerificationFields}
        />)}
        {column.rows?.length ? <FormRows
          rows={column.rows}
          tabName={tabName}
          values={values}
          errors={errors}
          readOnly={readOnly}
          onFieldChange={onFieldChange}
          onAction={onAction}
          interactionDisabled={interactionDisabled}
          secureSubmissionId={secureSubmissionId}
          submissionFileAccess={submissionFileAccess}
          omitVerificationFields={omitVerificationFields}
        /> : null}
      </div>)}
    </div>)}
  </>;
}

function visibleDataFields(form: DynamicFormDefinition, tabName: string, values: FormValues): FormField[] {
  return getTabFields(form, tabName).filter((field) =>
    isDataField(field) && !field.hidden && !field.disabled && evaluateVisibility(field.visibility, values)
  );
}

export function FormRenderer({ form, initialValues, submittedFormId = 0, readOnly = false, showAllReadOnlyTabs = false, omitVerificationFields = false, moduleId = 0, secureSubmissionId = 0, submissionFileAccess = 'owner', onSubmitted }: Props) {
  const { notify } = useFeedback();
  const { locale, formatNumber } = useUiLocale();
  const copy = publicCopy(locale);
  const [activeIndex, setActiveIndex] = useState(0);
  const [values, setValues] = useState<FormValues>(() => mergeValues(createInitialValues(form), initialValues));
  const [errors, setErrors] = useState<ErrorMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const submittingRef = useRef(false);
  const validatingRef = useRef(false);
  const rootRef = useRef<HTMLElement>(null);
  const firstInvalidRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setValues(mergeValues(createInitialValues(form), initialValues));
    setActiveIndex(0);
    setErrors({});
    setPreviewing(false);
    setSubmitting(false);
    setValidating(false);
    submittingRef.current = false;
    validatingRef.current = false;
  }, [form, initialValues]);

  const activeTab = form.Tabs[activeIndex] ?? form.Tabs[0];
  const isLast = activeIndex >= form.Tabs.length - 1;
  const progress = form.Tabs.length ? Math.round(((activeIndex + 1) / form.Tabs.length) * 100) : 100;

  const tabValidity = useMemo(() => form.Tabs.map((tab) => visibleDataFields(form, tab.name, values).every((field) => validateFieldSync(field, values[tab.name]?.[field.name], locale).length === 0)), [form, locale, values]);

  const setFieldValue = (tabName: string, fieldName: string, value: unknown) => {
    setValues((current) => ({ ...current, [tabName]: { ...(current[tabName] ?? {}), [fieldName]: value } }));
    setErrors((current) => {
      const next = { ...current };
      delete next[path(tabName, fieldName)];
      return next;
    });
  };

  const validateTab = async (tabName: string): Promise<boolean> => {
    const nextErrors: ErrorMap = {};
    const effectiveValues = applyCalculatedValues(form, values);
    const fields = visibleDataFields(form, tabName, effectiveValues);
    for (const field of fields) {
      const value = effectiveValues[tabName]?.[field.name];
      const fieldErrors = validateFieldSync(field, value, locale);
      const empty = value == null || (Array.isArray(value) ? value.length === 0 : String(value).trim() === '');
      if (!empty && !fieldErrors.length) {
        if (field.validation?.unique) {
          try {
            const duplicate = await validateUnique(Number(form.Id), field.name, String(value), submittedFormId);
            if (duplicate) fieldErrors.push({ key: 'unique', message: field.validation.uniqueMessage || copy.uniqueValueUsed });
          } catch { fieldErrors.push({ key: 'unique', message: copy.uniqueValidationFailed }); }
        }
        if (field.validation?.remoteUrl) {
          try {
            const valid = await validateRemote(field.validation.remoteUrl, Number(form.Id), field.name, String(value));
            if (!valid) fieldErrors.push({ key: 'remote', message: field.validation.remoteMessage || copy.valueNotConfirmed });
          } catch { fieldErrors.push({ key: 'remote', message: field.validation.remoteMessage || copy.remoteValidationFailed }); }
        }
        if (field.type === 'simplecaptcha') {
          try {
            const answer = normalizeSimpleCaptchaInput(String(value), resolveSimpleCaptchaMode(field));
            const state = getSimpleCaptchaChallenge(field.id, answer);
            if (!state.challengeId) {
              fieldErrors.push({ key: 'captcha', message: copy.captchaExpired });
            } else if (!state.verified) {
              const valid = await validateCaptcha(answer, state.challengeId);
              if (valid) markSimpleCaptchaVerified(field.id, state.challengeId, answer);
              else fieldErrors.push({ key: 'captcha', message: copy.captchaIncorrect });
            }
          } catch { fieldErrors.push({ key: 'captcha', message: copy.captchaUnavailable }); }
        }
      }
      if (fieldErrors.length) nextErrors[path(tabName, field.name)] = fieldErrors;
    }
    firstInvalidRef.current = Object.keys(nextErrors)[0];
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(next)) if (key.startsWith(`${tabName}.`)) delete next[key];
      return { ...next, ...nextErrors };
    });
    return Object.keys(nextErrors).length === 0;
  };

  const focusFirstInvalid = (tabName: string) => {
    window.requestAnimationFrame(() => {
      const firstKey = firstInvalidRef.current;
      const fieldName = firstKey?.startsWith(`${tabName}.`) ? firstKey.slice(tabName.length + 1) : undefined;
      const nodes = rootRef.current?.querySelectorAll<HTMLElement>('[data-field-name]');
      const wrapper = nodes ? Array.from(nodes).find((node) => node.dataset.fieldName === fieldName) : undefined;
      wrapper?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      wrapper?.querySelector<HTMLElement>('input,select,textarea,button')?.focus();
    });
  };

  const doSubmit = async () => {
    if (readOnly || submittingRef.current || validatingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      for (let index = 0; index < form.Tabs.length; index++) {
        const valid = await validateTab(form.Tabs[index]!.name);
        if (!valid) {
          setActiveIndex(index);
          setPreviewing(false);
          notify(copy.fixFormErrors, 'warning');
          window.setTimeout(() => focusFirstInvalid(form.Tabs[index]!.name), 0);
          return;
        }
      }
      const calculatedValues = applyCalculatedValues(form, values);
      const normalizedValues = normalizeValuesForSubmit(form, calculatedValues);
      const submitCode = await submitForm(Number(form.Id), normalizedValues, submittedFormId, moduleId);
      setValues(normalizedValues);
      onSubmitted?.(submitCode, normalizedValues);
      notify(submittedFormId ? copy.formSavedNotice : copy.formSubmittedTitle, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : copy.submitFailed, 'error');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    if (!activeTab || submittingRef.current || validatingRef.current) return;
    validatingRef.current = true;
    setValidating(true);
    let valid = false;
    try {
      valid = await validateTab(activeTab.name);
    } finally {
      validatingRef.current = false;
      setValidating(false);
    }
    if (!valid) {
      notify(copy.fixStepErrors, 'warning');
      window.setTimeout(() => focusFirstInvalid(activeTab.name), 0);
      return;
    }
    if (activeTab.isPreviewEnable) {
      setPreviewing(true);
    } else if (isLast) {
      await doSubmit();
    } else {
      setActiveIndex((index) => Math.min(form.Tabs.length - 1, index + 1));
    }
  };

  const interactionLocked = submitting || validating;
  const action = (kind: NonNullable<FormField['buttonAction']>) => {
    if (interactionLocked) return;
    if (kind === 'next') void goNext();
    else if (kind === 'previous') { setPreviewing(false); setActiveIndex((index) => Math.max(0, index - 1)); }
    else if (kind === 'submit') void doSubmit();
  };

  if (!activeTab) return <div className="alert alert-warning">{copy.formHasNoSteps}</div>;

  if (readOnly && showAllReadOnlyTabs) {
    return <section ref={rootRef} className={`df-form-renderer df-form-renderer--readonly-all ${omitVerificationFields ? 'df-form-renderer--without-verification' : ''}`}>
      {form.Tabs.map((tab, index) => { const showHeading = shouldShowTabHeading(form, tab.title); return <div className={`panel card df-panel panel-default df-readonly-tab ${showHeading ? '' : 'df-panel--without-heading'}`} data-tab-name={tab.name} key={tab.name}>
        {showHeading ? <div className="panel-heading card-header df-readonly-tab__heading" style={tabVisualStyle(tab)}><strong>{tab.title}</strong><span className="df-readonly-tab__number">{formatNumber(index + 1)}</span></div> : null}
        <div className="panel-body card-body">
          <FormRows rows={tab.rows} tabName={tab.name} values={values} readOnly onFieldChange={() => undefined} interactionDisabled secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} omitVerificationFields={omitVerificationFields} />
        </div>
      </div>; })}
    </section>;
  }

  if (previewing) {
    const previewTabs = form.Tabs.slice(0, activeIndex + 1);
    return <section ref={rootRef} className="df-form-preview">
      <div className="df-preview-heading"><div><h3>{copy.previewTitle}</h3><p>{copy.previewHelp}</p></div><span className="label badge label-info bg-info">{formatNumber(activeIndex + 1)} {copy.of} {formatNumber(form.Tabs.length)}</span></div>
      {previewTabs.map((tab) => { const showHeading = shouldShowTabHeading(form, tab.title); return <div className={`panel card df-panel panel-default ${showHeading ? '' : 'df-panel--without-heading'}`} key={tab.name}>
        {showHeading ? <div className="panel-heading card-header" style={tabVisualStyle(tab)}><strong>{tab.title}</strong></div> : null}
        <div className="panel-body card-body">
          <FormRows rows={tab.rows} tabName={tab.name} values={values} readOnly onFieldChange={() => undefined} interactionDisabled secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} omitVerificationFields={omitVerificationFields} />
        </div>
      </div>; })}
      <div className="df-form-actions">
        <button type="button" className="btn btn-default" disabled={interactionLocked} onClick={() => setPreviewing(false)}>{copy.backAndEdit}</button>
        {isLast
          ? <button type="button" className="btn btn-success" disabled={interactionLocked} onClick={() => void doSubmit()}>{submitting ? copy.submitting : copy.confirmAndSubmit}</button>
          : <button type="button" className="btn btn-primary" disabled={interactionLocked} onClick={() => { setPreviewing(false); setActiveIndex((index) => Math.min(form.Tabs.length - 1, index + 1)); }}>{copy.confirmAndContinue}</button>}
      </div>
    </section>;
  }

  return <section ref={rootRef} className="df-form-renderer">
    {form.Tabs.length > 1 ? <>
      <div className="progress df-progress" aria-label={copy.completionProgress}><div className="progress-bar progress-bar-success bg-success" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} style={{ width: `${progress}%` }}>{formatNumber(progress)}%</div></div>
      <ol className="breadcrumb df-step-breadcrumb" aria-label={copy.formSteps}>{form.Tabs.map((tab, index) => <li key={tab.name} className={`${index === activeIndex ? 'active' : ''} ${index < activeIndex && tabValidity[index] ? 'is-valid' : ''}`}><button type="button" style={tabVisualStyle(tab)} aria-current={index === activeIndex ? 'step' : undefined} disabled={interactionLocked || (!readOnly && index > activeIndex + 1)} onClick={() => { if (readOnly || index <= activeIndex) { setPreviewing(false); setActiveIndex(index); } else if (index === activeIndex + 1) { void goNext(); } }}>{tab.title}</button></li>)}</ol>
    </> : null}

    <div className={`panel card df-panel panel-default ${shouldShowTabHeading(form, activeTab.title) ? '' : 'df-panel--without-heading'}`}>
      {shouldShowTabHeading(form, activeTab.title) ? <div className="panel-heading card-header" style={tabVisualStyle(activeTab)}><strong>{activeTab.title}</strong></div> : null}
      <div className="panel-body card-body">
        <FormRows
          rows={activeTab.rows}
          tabName={activeTab.name}
          values={values}
          errors={errors}
          readOnly={readOnly}
          onFieldChange={(name, next) => setFieldValue(activeTab.name, name, next)}
          onAction={action}
          interactionDisabled={interactionLocked}
          secureSubmissionId={secureSubmissionId}
          submissionFileAccess={submissionFileAccess}
          omitVerificationFields={omitVerificationFields}
        />
      </div>
    </div>

    {!readOnly ? <div className="df-form-actions">
      {activeIndex > 0 && activeTab.isReturnEnable ? <button type="button" className="btn btn-default" disabled={interactionLocked} onClick={() => setActiveIndex((index) => Math.max(0, index - 1))}>{copy.previousStep}</button> : <span />}
      {!isLast
        ? <button type="button" className="btn btn-primary" disabled={interactionLocked} onClick={() => void goNext()}>{validating ? copy.validating : copy.nextStep}</button>
        : <button type="button" className="btn btn-success" disabled={interactionLocked} onClick={() => void goNext()}>{submitting ? copy.submitting : validating ? copy.validating : activeTab.isPreviewEnable ? copy.previewAndContinue : submittedFormId ? copy.saveChanges : copy.submitForm}</button>}
    </div> : null}
  </section>;
}
